import { PropsWithChildren, useCallback, useMemo } from 'react';

import { UnScheduledEmployeePunch } from '@sbv-clients/data-access-api';
import {
  Button,
  DataGridPremium,
  GridColDef,
  GridRowIdGetter,
} from '@sbv-clients/ui-shared';

import { DeparturesResponseType } from '../../../../services/dashboard';
import { OpenUnScheduleModalType } from '../unscheduled-modal';

interface ArrivalsTableProps {
  data: DeparturesResponseType[];
  unScheduledDepartures: UnScheduledEmployeePunch[];
  openModal: OpenUnScheduleModalType;
  isLoading: boolean;
}

const TextButton = ({
  children,
  onClick,
}: {
  onClick: () => void;
} & PropsWithChildren) => {
  return (
    <Button size="small" variant="text" sx={{ p: 1 }} onClick={onClick}>
      {children || 0}
    </Button>
  );
};

const ArrivalsTable = ({
  data,
  unScheduledDepartures,
  openModal,
  isLoading,
}: ArrivalsTableProps) => {
  const columns: GridColDef<DeparturesResponseType>[] = useMemo(() => {
    return [
      {
        field: 'endTime',
        headerName: 'End Time',
        sortable: false,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value) =>
          value === 'Unscheduled' ? 'Unscheduled' : value,
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.endTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() =>
                openModal('scheduled', params.row.scheduledEmployees as any)
              }
            >
              {params.row.scheduled || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'out',
        headerName: 'Out',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.endTime === 'Unscheduled') {
            return (
              <TextButton
                onClick={() =>
                  openModal('unscheduled-departures', unScheduledDepartures)
                }
              >
                {unScheduledDepartures.length}
              </TextButton>
            );
          }
          return (
            <TextButton
              onClick={() => openModal('out', params.row.outEmployees as any)}
            >
              {params.row.out || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'difference',
        headerName: 'Difference',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.endTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() =>
                openModal('difference', params.row.differenceEmployees as any)
              }
            >
              {params.row.difference || 0}
            </TextButton>
          );
        },
      },
      {
        field: 'overtime',
        headerName: 'Overtime',
        sortable: false,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (params.row.endTime === 'Unscheduled') {
            return '-';
          }
          return (
            <TextButton
              onClick={() =>
                openModal('overtime', params.row.overtimeEmployees as any)
              }
            >
              {params.row.overtime || 0}
            </TextButton>
          );
        },
      },
    ] as GridColDef<DeparturesResponseType>[];
  }, [openModal, unScheduledDepartures]);

  const getRowId: GridRowIdGetter<DeparturesResponseType> = useCallback(
    (row) => row.endTime as string,
    []
  );

  return (
    <DataGridPremium
      rows={data}
      columns={columns}
      disableRowSelectionOnClick
      getRowId={getRowId}
      disableColumnMenu
      hideFooterPagination
      hideFooter
      autoHeight
      loading={isLoading}
    />
  );
};

export default ArrivalsTable;
